import { SentryError } from '@ifixit/sentry';
import type { Schema, Node, Mark } from '@tiptap/pm/model';
import { fixedGridColumns } from './grid_columns';

class WikiTextSerializer {
   private nodes: object;
   private marks: object;

   constructor(nodes: object, marks: object) {
      this.nodes = nodes;
      this.marks = marks;
   }

   serialize(content: Node): string {
      const schema = content.type.schema;
      const state = new WikiTextSerializerState(this.nodes, this.marks, schema);

      state.renderDoc(content);
      return state.getOut().trim();
   }
}

/**
 * The server keeps link text verbatim (Text_Wiki_Parse_Url::processDescribed),
 * so markup inside [link|url|...], [[Page|...]] or [guide|1|...] saves as
 * literal quotes and asterisks. Other marks must wrap these, never sit inside
 * them.
 */
const LINK_MARK_NAMES = new Set(['link', 'wikiLink', 'siteLink']);

function isLinkMark(mark: Mark): boolean {
   return LINK_MARK_NAMES.has(mark.type.name);
}

class WikiTextSerializerState {
   private nodes: object;
   private marks: object;
   private prefix: string;
   private out: string;
   private currentlyOpenMarks: Array<Mark>;
   // Marks to write in place of a text node's own, set by pinSplitWikiLinks.
   private markOverrides = new Map<Node, ReadonlyArray<Mark>>();
   private spaces: string;
   private schema: Schema;

   constructor(nodes: object, marks: object, schema: Schema) {
      this.nodes = nodes;
      this.marks = marks;
      this.prefix = '';
      this.out = '';
      this.schema = schema;

      // Keep track of currently open marks so that we know when they need to
      // be closed.
      this.currentlyOpenMarks = [];
      // Store spaces from the end of a node to be output after the wiki markup
      this.spaces = '';
   }

   getOut(): string {
      return this.out;
   }

   getPrefix(): string {
      return this.prefix;
   }

   getSchema(): Schema {
      return this.schema;
   }

   append(text: string): void {
      this.out += text;
   }

   renderDoc(content: Node): void {
      content.forEach(n => this.render(n));
   }

   render(node: Node): void {
      // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const f = this.nodes[node.type.name];
      if (!f) {
         throw new SentryError('no function for ' + node.type.name);
      }
      f(this, node);
   }

   /**
    * Give a node, prefix, and a callback, replace the current prefix with the
    * characters from the given prefix and append the given prefix.
    *
    * This is mostly useful for rendering nested lists. If the current prefix
    * is '#' for an ordered lists and this is called with the prefix '*' for a
    * bullet list, the prefix will become '**' so that the new list will be
    * correctly rendered as a nested bullet list.
    *
    * After the callback has completed, restore the prefix to the old value.
    */
   // FIXME: Don't use `Function` as a type. The `Function` type accepts any function-like value.
   // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
   renderPrefix(node: Node, prefix: string, func: Function, suffix: string = ''): void {
      const oldPrefix = this.prefix;
      const prefixDepth = oldPrefix.length + prefix.length;
      this.prefix = prefix.repeat(prefixDepth) + suffix;

      func();

      this.prefix = oldPrefix;
   }

   // Prefix a node's text with the given list syntax.
   renderList(node: Node, prefix: string): void {
      // Need to run renderPrefix() on each child of node.
      // In this case node is either the ordered_list or bullet_list node, and
      // prefix is either '#' or '*' based on the node type.
      //
      // Also the function given to prefix can use the child node type to
      // determine whether or not to add a space to state.out
      node.forEach(child => {
         this.renderPrefix(child, prefix, () => {
            child.forEach(lineItemChild => this.render(lineItemChild));
         });
      });
   }

   renderRestartedList(node: Node): void {
      const firstChild = node.firstChild!;
      if (!firstChild) {
         return;
      }
      this.renderPrefix(
         firstChild,
         '#',
         () => {
            firstChild.forEach(lineItemChild => this.render(lineItemChild));
         },
         `${node.attrs.start}.`
      );
      node.forEach((child, _offset, index) => {
         if (index === 0) {
            // Special-case first child.
            return;
         }
         this.renderPrefix(child, '#', () => {
            child.forEach(lineItemChild => this.render(lineItemChild));
         });
      });
   }

   // Apply marks to inline text. This involves the following two steps:
   // 1. Do a first pass over the nodes to get the length of each continuous
   // mark. Save this information in a Map for the second pass. (This
   // can't be done simultaneously with the next step, because we need to know
   // the full length of each mark in order to apply them in the right order.)
   // 2. For each text node (this is done in the renderer for text nodes):
   //   1. Find closing marks
   //   2. Pop marks off the stack until all the closing marks are gone. Close
   //   all marks popped off.
   //   3. Generate start characters for all marks present on this node and
   //   not already on stack. Push onto the stack in the order generated. This
   //   will include any marks popped in step 2 which are supposed to be on the
   //   node.
   //
   /**
    * A bare wikilink ([[E-Waste]]) names its page by its text, so once the
    * link-innermost rule splits it around formatting, each piece would link
    * to its own fragment ([[E-]] ***[[Waste]]***). A text node only differs
    * from its neighbor when its marks do, so a bare wikilink spanning two or
    * more text nodes always splits: pin every piece to the whole text.
    */
   pinSplitWikiLinks(node: Node): void {
      let run: Node[] = [];
      let runMark: Mark | null = null;
      const flush = () => {
         if (runMark && run.length > 1) {
            const title = run.map(piece => piece.text ?? '').join('');
            const pinned = runMark.type.create({ ...runMark.attrs, target: title });
            for (const piece of run) {
               this.markOverrides.set(
                  piece,
                  piece.marks.map(mark => (mark.eq(runMark!) ? pinned : mark))
               );
            }
         }
         run = [];
         runMark = null;
      };
      node.forEach(child => {
         const bare = child.isText
            ? child.marks.find(mark => mark.type.name === 'wikiLink' && mark.attrs.target === null)
            : undefined;
         if (bare && runMark && bare.eq(runMark)) {
            run.push(child);
            return;
         }
         flush();
         if (bare) {
            runMark = bare;
            run = [child];
         }
      });
      flush();
   }

   inline(node: Node): void {
      this.pinSplitWikiLinks(node);
      const currentMarkLengths = new Map();
      const nodeMarkLengths = new Map();
      // Compute mark lengths
      const findMarkLengths = (node: any) => {
         // Exit early if there are no marks.
         if (!node.marks.length) {
            currentMarkLengths.forEach((v, k) => currentMarkLengths.delete(k));
            nodeMarkLengths.set(node, new Map());
            return;
         }

         // Gather all the marks on this node (by name, so they will match the
         // marks from previous nodes).
         const marks = node.marks.map((m: any) => m.type.name);

         // Marks that exist for the current node, but not in currentMarkLengths
         // are new marks, so they should be added and set to 0. We'll add the
         // length of the current text to both the new and old marks next.
         marks.forEach((m: any) => {
            if (!currentMarkLengths.has(m)) {
               currentMarkLengths.set(m, 0);
            }
         });
         currentMarkLengths.forEach((v, k) =>
            currentMarkLengths.set(k, node.text ? v + node.text.length : 0)
         );

         // Marks in currentMarkLengths that do not exist on the current node are no
         // longer needed. Remove them. The ones that do exist, however, should
         // be in the list of lengths for the marks on this node.
         const lengths = new Map();
         currentMarkLengths.forEach((v, k) => {
            if (marks.includes(k)) {
               lengths.set(k, v);
            } else {
               currentMarkLengths.delete(k);
            }
         });
         // Store the list of lengths for later use by the renderer.
         nodeMarkLengths.set(node, lengths);
      };

      const children: Array<Node> = [];
      node.forEach(c => c.isText && children.push(c));
      children.reverse();
      children.forEach(findMarkLengths);

      node.forEach(child => this.text(child, nodeMarkLengths));

      this.end_inline();
   }

   end_inline(): void {
      // After all nodes have been handled, close any marks that are still open
      this.closeMarks(this.currentlyOpenMarks);
      this.currentlyOpenMarks = [];
      // Throw away spaces that were at the end of the text. We don't need them.
      this.spaces = '';
   }

   /**
    * Render a text node to the output
    *
    * If marklengths is available, it will be used to decide in what order to
    * apply marks to the text
    */
   text(node: Node, marklengths?: any): void {
      const isOpen = (mark: Mark) => this.currentlyOpenMarks.some(o => mark.eq(o));
      // Leading whitespace is written before marks open, so a space that
      // (re)opens a link saves as an empty one, [link|url|]: the space
      // between two separately formatted words of one link does this. A
      // space keeps its link only while the link stays open around it.
      const nodeMarks = this.markOverrides.get(node) ?? node.marks;
      const closesAroundLink = this.currentlyOpenMarks.some(
         open => !isLinkMark(open) && !nodeMarks.some(m => open.eq(m))
      );
      const marks =
         node.isText && !node.text?.trim()
            ? nodeMarks.filter(mark => !isLinkMark(mark) || (isOpen(mark) && !closesAroundLink))
            : nodeMarks;

      // Searches haystack for values in needles and returns the lowest index
      // found (i.e., the index of the leftmost).
      const firstNeedle = function (haystack: any, needles: any) {
         const result = needles.reduce(
            (min: any, needle: any) => Math.min(min, haystack.indexOf(needle)),
            haystack.length
         );
         if (result === -1) {
            throw new SentryError(
               "Don't use this function on `needles` that " + 'may not be in `haystack`'
            );
         }
         return result;
      };

      // Link marks stay innermost, so a mark opening around an open link
      // closes the link first and reopens it inside the new mark.
      const opensOuterMark = marks.some(m => !isLinkMark(m) && !isOpen(m));

      // If currentlyOpenMarks includes marks that do not exist on the current
      // node, close those marks before adding new marks and the inline text to
      // the output.
      const toClose = this.currentlyOpenMarks.filter(
         openMark => !marks.some(m => openMark.eq(m)) || (opensOuterMark && isLinkMark(openMark))
      );
      // We need to close all marks that were opened after the oldest mark we
      // need to close, so that we don't get overlapping marks (i.e. `<i>italic
      // <b>italic bold</i> bold</b>`, but in wiki text).
      const oldestClose = firstNeedle(this.currentlyOpenMarks, toClose);

      this.closeMarks(this.currentlyOpenMarks.slice(oldestClose));
      // Remove closed marks from openMarks.
      this.currentlyOpenMarks = this.currentlyOpenMarks.slice(0, oldestClose);

      // Marks that exist for the current node, but not in currentlyOpenMarks
      // should be opened. This will include marks that were closed to get to
      // the earliest close mark, since they won't be in currentlyOpenMarks.
      const toOpen = marks.filter(mark => !isOpen(mark));
      // If we have length information available, use it.
      if (marklengths) {
         const lengths = marklengths.get(node);
         if (lengths) {
            // For efficiency: open the marks that will stay open the longest first.
            toOpen.sort((a, b) => lengths.get(b.type.name) - lengths.get(a.type.name));
         }
      }
      // Stable, so the length order above holds among the other marks.
      toOpen.sort((a, b) => Number(isLinkMark(a)) - Number(isLinkMark(b)));

      this.currentlyOpenMarks = this.currentlyOpenMarks.concat(toOpen);
      if (node.isText && node.text) {
         // Borrows from src/to_markdown.js from prosemirror-markdown
         const [, start, text, end]: ReadonlyArray<string | undefined> =
            node.text.match(/^(\s*)([^]*?)(\s*)$/) || [];
         // A site link still showing the name the server looked up saves
         // without text, so it keeps following that name.
         const hidesText = toOpen.some(
            mark => mark.type.name === 'siteLink' && mark.attrs.defaultText === text
         );
         this.out += this.spaces;
         this.out += start;
         this.openMarks(toOpen, hidesText);
         if (!hidesText) {
            this.out += text;
         }
         this.spaces = end;
      } else {
         this.out += this.spaces;
         this.spaces = '';
         this.openMarks(toOpen);
         this.render(node);
      }
   }

   closeMarks(marks: ReadonlyArray<Mark>) {
      this.out += marks.reduceRight((carry, mark) => {
         // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
         const close = this.marks[mark.type.name]?.close;
         if (!close) {
            throw new SentryError('mark not found: ' + mark.type.name);
         }
         const markText = typeof close === 'function' ? close(mark) : close;

         return carry + markText;
      }, '');
   }

   openMarks(marks: any, hidesText = false): void {
      this.out += marks.reduce((carry: any, mark: any) => {
         // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
         const open = this.marks[mark.type.name]?.open;
         if (!open) {
            throw new SentryError('mark not found: ' + mark.type.name);
         }
         const markText = typeof open === 'function' ? open(mark, hidesText) : open;

         return carry + markText;
      }, '');
   }
}

const serializer = new WikiTextSerializer(
   {
      // Nodes
      paragraph(state: WikiTextSerializerState, node: Node): void {
         const prefix = state.getPrefix();
         if (prefix) {
            state.append(prefix + ' ');
         }

         state.inline(node);
         state.append('\n');

         if (!prefix) {
            state.append('\n');
         }
      },

      text(state: WikiTextSerializerState, node: Node): void {
         state.text(node);
         // A lone text node is in effect a very short inline.
         state.end_inline();
      },

      customHardBreak(state: WikiTextSerializerState, _node: Node): void {
         state.append('[br]\n');
      },

      heading(state: WikiTextSerializerState, node: Node): void {
         const headerTag = '='.repeat(node.attrs.level);
         state.append(headerTag + ' ');
         state.inline(node);
         state.append(' ' + headerTag + '\n');
      },

      image(state: WikiTextSerializerState, node: Node): void {
         const { imageid, align, size } = node.attrs;
         state.append(`[image|${imageid}|align=${align}|size=${size}]`);
      },

      // Mirrors Text_Wiki_Render_Json_Image::toWikitext, so an image nobody
      // touched saves back byte for byte.
      wikiImage(state: WikiTextSerializerState, node: Node): void {
         const { imageid, size, align, caption, link, newWindow } = node.attrs;
         const args = [
            size && size !== 'standard' ? `|size=${size}` : '',
            align === 'left' ? '' : `|align=${align}`,
            caption === null ? '' : `|caption=${caption}`,
            link === null ? '' : `|link=${link}`,
            newWindow ? '|new_window=true' : '',
         ];
         state.append(`[image|${imageid}${args.join('')}]`);
      },

      codeBlock(state: WikiTextSerializerState, node: Node): void {
         state.append('[code]\n');
         state.inline(node);
         state.append('\n[/code]\n');
      },

      table(state: WikiTextSerializerState, node: Node): void {
         state.inline(node);
         state.append('\n');
      },

      blockquote(state: WikiTextSerializerState, node: Node): void {
         let attribute = node.attrs.attribute;
         const format = node.attrs.format;
         const attrSpec = node.type.spec.attrs;

         if (attribute === 'null') {
            attribute = null;
         }

         state.append('[quote');

         if (attrSpec) {
            // A blockquote attribute can be rich text, so we store it in a node
            // attribute and then built it into an actual node using
            // schema.nodeFromJSON.
            if (attribute && attribute !== attrSpec.attribute.default) {
               state.append('|');

               attribute = state.getSchema().nodeFromJSON(attribute);
               state.inline(attribute);
            }

            if (format && format !== attrSpec.format.default) {
               state.append('|format=' + format);
            }
         }

         state.append(']\n');
         state.inline(node);
         state.append('[/quote]\n');
      },

      featuredQuote(state: WikiTextSerializerState, node: Node): void {
         state.append('[quote|format=featured]\n');
         state.inline(node);
         state.append('[/quote]\n');
      },

      grid(state: WikiTextSerializerState, node: Node): void {
         const columns = fixedGridColumns(node.attrs.columns);
         if (columns === null) {
            state.append('[grid]\n');
         } else {
            state.append(`[grid|columns=${columns}]\n`);
         }
         state.inline(node);
         state.append('[/grid]\n');
      },

      orderedList(state: WikiTextSerializerState, node: Node): void {
         if (node.attrs.start === 1) {
            state.renderList(node, '#');
         } else {
            state.renderRestartedList(node);
         }
      },

      bulletList(state: WikiTextSerializerState, node: Node): void {
         state.renderList(node, '*');
      },

      mention(state: WikiTextSerializerState, node: Node): void {
         state.append(`@${node.attrs.id}`);
      },

      note(state: WikiTextSerializerState, node: Node): void {
         const type = node.attrs.type;
         if (type === 'note') {
            state.append('[note]\n');
         } else {
            state.append(`[note|type=${node.attrs.type}]\n`);
         }
         state.inline(node);
         state.append('[/note]\n');
      },
   },
   {
      // Marks
      italic: {
         open: "''",
         close: "''",
      },
      bold: {
         open: '***',
         close: '***',
      },
      underline: {
         open: '++',
         close: '++',
      },
      subscript: {
         open: ',,',
         close: ',,',
      },
      superscript: {
         open: '^^',
         close: '^^',
      },
      code: {
         open: '``',
         close: '``',
      },
      strike: {
         open: '~~',
         close: '~~',
      },
      link: {
         open: (mark: Mark): string => '[link|' + mark.attrs.href + '|',
         close: (mark: Mark): string =>
            mark.attrs.target === '_blank' ? '|new_window=true]' : ']',
      },
      // [[Title]] when target is null (the text names the page) or
      // [[target|text]]; tag/new_window ride in front of the close.
      wikiLink: {
         open: (mark: Mark): string =>
            mark.attrs.target === null ? '[[' : '[[' + mark.attrs.target + '|',
         close: (mark: Mark): string => {
            const tag = mark.attrs.tag === null ? '' : '|tag=' + mark.attrs.tag;
            const newWindow = mark.attrs.newWindow ? '|new_window=true' : '';
            return tag + newWindow + ']]';
         },
      },
      // [guide|307|text|stepid=12], or [guide|307|stepid=12] when the text is
      // the guide's own title (see text()). Documents too: [document|12|text].
      siteLink: {
         open: (mark: Mark, hidesText: boolean): string =>
            '[' + mark.attrs.kind + '|' + mark.attrs.id + (hidesText ? '' : '|'),
         // In the order the server writes each token (Json/Ifixit/*link.php),
         // so an untouched link saves unchanged: a product puts new_window
         // before its type, a guide after its step. A document has no
         // new_window; its parser would drop it.
         close: (mark: Mark): string => {
            const newWindow =
               mark.attrs.newWindow && mark.attrs.kind !== 'document' ? '|new_window=true' : '';
            return mark.attrs.kind === 'product'
               ? newWindow + mark.attrs.options + ']'
               : mark.attrs.options + newWindow + ']';
         },
      },
   }
);

export default serializer;
